import { useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, LogIn, ShieldCheck, UserPlus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { qk, useSession } from "../api/queries";
import type { TotpSetup } from "../api/types";
import { Brand } from "../app/Brand";
import { LocaleMenu } from "../app/LocaleMenu";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { CodeBlock, LoadingRows } from "../ui/Common";
import { QrCode } from "../ui/QrCode";
import { inviteMessages } from "./invite.i18n";
import { loginMessages } from "./login.i18n";

const STORAGE_KEY = "ebk-invite";

/**
 * O segredo chega no fragmento (/invite#ebi1_…), que não vai ao servidor. Fica
 * guardado na aba enquanto a pessoa entra com uma conta existente.
 */
function readToken(): string {
  const hash = window.location.hash;
  const fromHash = hash.slice(hash.lastIndexOf("#") + 1);
  try {
    if (fromHash.startsWith("ebi1_")) {
      sessionStorage.setItem(STORAGE_KEY, fromHash);
      return fromHash;
    }
    return sessionStorage.getItem(STORAGE_KEY) ?? "";
  } catch {
    return fromHash.startsWith("ebi1_") ? fromHash : "";
  }
}

function forgetToken() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nada guardado */
  }
}

export function InvitePage() {
  const t = useT(inviteMessages);
  const tl = useT(loginMessages);
  const [token] = useState(readToken);

  useEffect(() => {
    // Tira o segredo da barra de endereço e do histórico.
    if (window.location.hash.startsWith("#ebi1_")) window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }, []);

  return (
    <div className="login">
      <aside className="login-side">
        <Brand />
        <div className="login-quote">
          <h2>{tl.headline}</h2>
          <p>{tl.pitch}</p>
        </div>
        <span />
      </aside>
      <div className="login-form">
        <div className="login-lang"><LocaleMenu variant="plain" /></div>
        <div className="login-card">
          {token ? <InviteFlow token={token} /> : <Unavailable message={t.missingToken} />}
        </div>
      </div>
    </div>
  );
}

function Unavailable({ message }: { message: string }) {
  const t = useT(inviteMessages);
  const navigate = useNavigate();
  return (
    <>
      <h1>{t.unavailableTitle}</h1>
      <p className="ink-2" role="alert">{message}</p>
      <button className="btn" type="button" onClick={() => navigate("/login")}>{t.goToLogin}</button>
    </>
  );
}

function InviteFlow({ token }: { token: string }) {
  const t = useT(inviteMessages);
  const tc = useT(common);
  const f = useFormat();
  const api = useApi();
  const info = useQuery({ queryKey: ["invitation", token], queryFn: () => api.lookupInvitation(token), retry: false, staleTime: Infinity });
  const session = useSession();

  if (info.isPending || session.isPending) return <LoadingRows rows={4} />;
  if (info.isError) return <Unavailable message={info.error instanceof ApiRequestError ? info.error.message : t.failed} />;
  const inv = info.data;

  return (
    <>
      <div>
        <h1>{t.title(inv.orgName)}</h1>
        <p className="ink-2" style={{ marginTop: 6 }}>{t.lede(tc.role[inv.role], inv.email)} {t.validUntil(f.dateTime(inv.expiresAt))}</p>
      </div>
      {inv.existingAccount
        ? <JoinExisting token={token} email={inv.email} signedInEmail={session.data?.user.email ?? null} />
        : <Signup token={token} defaultName={inv.name} email={inv.email} />}
    </>
  );
}

function JoinExisting({ token, email, signedInEmail }: { token: string; email: string; signedInEmail: string | null }) {
  const t = useT(inviteMessages);
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      const { orgId } = await api.joinInvitation(token);
      forgetToken();
      await qc.invalidateQueries({ queryKey: qk.orgs });
      navigate(`/o/${orgId}/overview`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed);
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    await api.logout();
    qc.clear();
  }

  if (!signedInEmail) {
    return (
      <>
        <p>{t.existingSignedOut}</p>
        <button className="btn btn-primary" type="button" style={{ height: 40 }} onClick={() => navigate("/login", { state: { from: "/invite" } })}>
          <LogIn aria-hidden="true" /> {t.signInToAccept}
        </button>
      </>
    );
  }
  if (signedInEmail.toLowerCase() !== email.toLowerCase()) {
    return (
      <>
        <p className="callout" role="alert">{t.wrongAccount(email)}</p>
        <button className="btn" type="button" onClick={() => void signOut()}>{t.signOut}</button>
      </>
    );
  }
  return (
    <>
      <p>{t.signedInAs(signedInEmail)}</p>
      {error && <p className="field error" role="alert">{error}</p>}
      <button className="btn btn-primary" type="button" style={{ height: 40 }} onClick={() => void accept()} disabled={busy}>
        <UserPlus aria-hidden="true" /> {busy ? t.working : t.accept}
      </button>
    </>
  );
}

function Signup({ token, defaultName, email }: { token: string; defaultName: string; email: string }) {
  const t = useT(inviteMessages);
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(defaultName);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [setup, setSetup] = useState<TotpSetup | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function begin(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError(t.mismatch);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setSetup(await api.beginInvitationSignup({ token, name, password }));
      setPassword("");
      setConfirm("");
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed);
    } finally {
      setBusy(false);
    }
  }

  async function activate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await api.activateInvitation({ token, code });
      forgetToken();
      qc.setQueryData(qk.session, s);
      navigate(s.homeOrgId ? `/o/${s.homeOrgId}/overview` : "/", { replace: true });
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : t.failed);
      // Cadastro descartado após tentativas demais: volta ao primeiro passo.
      if (err instanceof ApiRequestError && err.code === "signup_not_started") setSetup(null);
    } finally {
      setBusy(false);
    }
  }

  if (!setup) {
    return (
      <form onSubmit={begin} style={{ display: "grid", gap: 16 }} noValidate>
        <h2 style={{ fontSize: "var(--fs-16)" }}>{t.createTitle}</h2>
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <div className="field">
          <label htmlFor="inv-name">{t.name}</label>
          <input id="inv-name" className="input" autoComplete="name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="inv-password">{t.password}</label>
          <input id="inv-password" className="input" type="password" autoComplete="new-password" minLength={12} maxLength={256} value={password} onChange={(e) => setPassword(e.target.value)} required />
          <span className="hint">{t.passwordHint}</span>
        </div>
        <div className="field">
          <label htmlFor="inv-confirm">{t.confirmPassword}</label>
          <input id="inv-confirm" className="input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
        </div>
        {error && <p className="field error" role="alert">{error}</p>}
        <button className="btn btn-primary" type="submit" style={{ height: 40 }} disabled={busy || !name.trim() || password.length < 12}>
          <KeyRound aria-hidden="true" /> {busy ? t.working : t.continue}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={activate} style={{ display: "grid", gap: 16 }} noValidate>
      <div>
        <h2 style={{ fontSize: "var(--fs-16)" }}>{t.totpTitle}</h2>
        <p className="ink-2" style={{ marginTop: 6 }}>{t.totpLede}</p>
      </div>
      <div style={{ display: "grid", justifyItems: "center" }}>
        <QrCode value={setup.otpauthUri} label={t.qrLabel} />
      </div>
      <div className="field">
        <span className="hint">{t.manualKey}</span>
        <CodeBlock code={setup.secret} label={t.secretLabel} />
      </div>
      <div className="field">
        <label htmlFor="inv-code">{t.code}</label>
        <input id="inv-code" className="input code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus required />
      </div>
      {error && <p className="field error" role="alert">{error}</p>}
      <button className="btn btn-primary" type="submit" style={{ height: 40 }} disabled={busy || code.length !== 6}>
        <ShieldCheck aria-hidden="true" /> {busy ? t.working : t.activate}
      </button>
    </form>
  );
}
